import { Fragment, useContext, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import FormContainer from "components/form-container";
import ProgressBar from "components/progress-bar";
import {
  useLocation,
  useOutletContext,
  createSearchParams,
  useNavigate,
} from "react-router-dom";
import BrickIcon from "assets/brickIcon.svg";
import HeadIcon from "assets/headIcon.svg";
import WeightEstimation from "components/weight-estimation";
import DonationWeightChart from "components/donation-weight-chart";
import "material-symbols/outlined.css";
import {
  localizeDecimalSeparator,
  getQueryParams,
  useCurrentLocation,
  checkIfFullKiosk,
  calculateDurationByWeight,
} from "lib/utils";
import { useRestartIfAbsent } from "hooks/useRestartIfAbsent";
import { calculateEstimate } from "lib/calculateEstimate";
import { useCreateTakeback, useUpdateTakeback } from "api/use-takebacks";
import { useHasTakeback } from "api/use-has-takeback";
import AuthContext from "contexts/AuthContext";
import { TAKEBACK_METHOD_NAME, TAKEBACK_ID } from "consts/query-params";
import storeLocations from "consts/stores.json";
import useWhen from "hooks/useWhen";
import { END_OF_PILOT_ARGS, PAYMENT_ARGS } from "consts/feature-config";
import { isValidWeight, isValidPayoutMethod } from "lib/takeback-validations";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  confirmAppointment,
  checkAppointmentStillValid,
} from "components/appointments/service";
import FloodlightEvent from "components/floodlight-event";
// import { isDropoffStore } from "lib/utils";
import { EVENT_13, EVENT_14 } from "utils/tracking";

// Extend dayjs with timezone plugins
dayjs.extend(utc);
dayjs.extend(timezone);

const extractStateFromReturnType = (returnType) => {
  if (!returnType) return null;
  const match = returnType.match(/in_store_(\w{2})/i);
  return match ? match[1].toUpperCase() : null;
};

const getTimezoneForState = (state) => {
  const timezoneMap = {
    CO: "America/Denver", // MDT
    AR: "America/Chicago", // CDT
    NJ: "America/New_York", // EDT
    PA: "America/New_York", // EDT
  };

  // Extract state from in_store_XX format
  const extractedState = extractStateFromReturnType(state);

  return timezoneMap[extractedState] || dayjs.tz.guess();
};

const getTimezoneAbbreviationForState = (returnType) => {
  const state = extractStateFromReturnType(returnType);
  switch (state) {
    case "CO":
      return "MDT";
    case "AR":
      return "CDT";
    case "NJ":
    case "PA":
      return "EDT";
    default:
      return "";
  }
};

// Format appointment time to show only the start time
const formatAppointmentTime = (startISO, returnType) => {
  if (!startISO) return null;
  const timezone = getTimezoneForState(returnType);
  const tzAbbr = getTimezoneAbbreviationForState(returnType);
  const startObj = dayjs(startISO).tz(timezone);
  // Example: Monday, September 15 at 3:30 PM MDT
  return (
    startObj.format("dddd, MMMM D") +
    " at " +
    startObj.format("h:mm A") +
    (tzAbbr ? ` ${tzAbbr}` : "")
  );
};

const WeightEstimationComponent = ({
  className,
  queryParams,
  currentLocation,
  remainingWeight,
  estimate,
  weightEstimate,
  unit,
  methodName,
  replayWeight,
}) => {
  const { t } = useTranslation();
  return (
    <WeightEstimation
      className={className}
      visual={queryParams.p === "giftcard" ? "giftcard" : "cash"}
      title={"Your reward will be worth"}
      locale={currentLocation.l}
      singleValue={
        queryParams[TAKEBACK_METHOD_NAME] === "mail"
          ? localizeDecimalSeparator(
              calculateEstimate(remainingWeight).gift,
              currentLocation
            )
          : localizeDecimalSeparator(estimate.gift, currentLocation)
      }
      animate
      description={`${
        queryParams[TAKEBACK_METHOD_NAME] === "mail"
          ? "Paid out as a LEGO® e-Gift Card."
          : `${t("ui.estimate_part2", {
              weight: weightEstimate,
              unit: unit,
            })}`
      }`}
      additionalDescription={
        queryParams[TAKEBACK_METHOD_NAME] === "mail" &&
        queryParams.replayPercentage &&
        parseFloat(queryParams.replayPercentage) > 0
          ? `Original estimate: $${estimate.gift} (${weightEstimate} ${unit})  •  Donation (${parseInt(queryParams.replayPercentage, 10)}%): ${replayWeight.toFixed(2)} ${unit}`
          : null
      }
    />
  );
};

export default function SummaryPage() {
  const { t } = useTranslation();
  const { setIsLoading } = useOutletContext();
  const [validationWarning, setValidationWarning] = useState(false);

  // Get query params and restart flow if absent
  const location = useLocation();
  const queryParams = getQueryParams(location.search);
  useRestartIfAbsent(
    queryParams,
    ["w", TAKEBACK_METHOD_NAME, "p"],
    t("urls.trade_in_weight")
  );

  // useRestartIfAbsent(
  //   queryParams,
  //   [APPOINTMENT_ID],
  //   t("urls.trade_in_appointment")
  // );

  //get current location settings
  const { location: currentLocation } = useCurrentLocation();

  const isMailMethod = queryParams[TAKEBACK_METHOD_NAME] === "mail";

  const parsedWeight = parseFloat(queryParams.w).toFixed(2);
  const weightEstimate =
    currentLocation?.locale !== "en-US"
      ? parsedWeight.toString().replace(".", ",")
      : parsedWeight;

  const estimate = calculateEstimate(queryParams.w);
  const unit = `${t("scale.weight")}`;

  //reset right side visual
  const { setSidebarVisual } = useOutletContext();
  useEffect(() => {
    setSidebarVisual(currentLocation.locale === "de-DE" ? "euro" : "dollar");
  }, [setSidebarVisual, currentLocation]);

  const matchingAddress = storeLocations.find(
    (location) => location.value === queryParams[TAKEBACK_METHOD_NAME]
  )?.address;
  const addressString = matchingAddress
    ? matchingAddress
    : t("trade-in.summary.address_not_found");
  const navigate = useNavigate();
  const onCreate = (params) => {
    setIsLoading(null);

    navigate(
      `${t("urls.trade_in_next_steps")}?${createSearchParams({
        ...queryParams,
        [TAKEBACK_ID]: params.sys_id,
      }).toString()}`
    );
  };
  const appointmentSlot = JSON.parse(
    localStorage.getItem("appointment") || "{}"
  );
  const appointment = appointmentSlot.appointment || {};

  const { mutateAsync: updateTakeback } = useUpdateTakeback();

  const { mutate: createTakeback } = useCreateTakeback({
    onCreate,
    confirmAppointment: async (sys_id) => {
      // Only confirm appointment if appointment.id exists
      if (appointment.id) {
        return confirmAppointment(appointment.id, sys_id, updateTakeback);
      }
      return Promise.resolve();
    },
  });

  // Auth
  const { user } = useContext(AuthContext);

  let userStreet = user?.street || "";

  // Replace \n with ", " unless it's at the end
  userStreet = userStreet.replace(/\n(?!$)/g, ", ");

  // Trim any trailing ", "
  userStreet = userStreet.replace(/, $/, "");

  //only show cash payment in appropriate environments
  const showPayment = useWhen(PAYMENT_ARGS);

  const methodName =
    queryParams.p === "giftcard"
      ? t("trade-in.summary.giftcardTitle")
      : t("trade-in.summary.cashTitle");

  // Perform validation checks for weight and payout method
  const weight = parseFloat(queryParams.w);
  const payoutMethod = queryParams.p;
  const returnMethod = queryParams[TAKEBACK_METHOD_NAME]; // "mail" or "instore"
  const remainingWeight = queryParams.replayPercentage
    ? queryParams.w * (1 - queryParams.replayPercentage / 100)
    : queryParams.w;

  const replayWeight =
    queryParams.replayPercentage && queryParams.replayPercentage > 0
      ? (queryParams.replayPercentage * weightEstimate) / 100
      : 0;

  const canEditDonation = !!queryParams.replayPercentage;

  //validate weight
  const isWeightValid = isValidWeight(
    weight,
    returnMethod,
    unit,
    currentLocation.state,
    replayWeight
  );

  // Determine which payout methods are valid
  const validPayoutMethods = showPayment ? ["cash", "giftcard"] : ["giftcard"];

  // walidate payout method
  const isPayoutMethodValid = isValidPayoutMethod(
    payoutMethod,
    validPayoutMethods
  );

  const returnTypeOffered = currentLocation.state
    ? currentLocation.state
    : currentLocation.country;

  const isKiosk = checkIfFullKiosk(currentLocation);

  const storeTakebackFinished = useWhen(END_OF_PILOT_ARGS);
  const isFormValid =
    isWeightValid &&
    isPayoutMethodValid &&
    (storeTakebackFinished ? returnMethod === "mail" : true);

  const { data: hasTakeback } = useHasTakeback();

  const isFirstTakeback = !hasTakeback;

  // const isDropoff = isDropoffStore(user);
  const giftRatio = calculateEstimate(1).giftRatio;

  return (
    <FormContainer
      onSubmit={() => {
        if (!isFormValid) {
          setValidationWarning(true);
          return;
        }

        if (!checkAppointmentStillValid("")) {
          setValidationWarning(true);
          return;
        }

        setValidationWarning(false);
        setIsLoading(t("trade-in.summary.loading_message"));
        createTakeback({
          uom: t("scale.weight"),
          weight: queryParams.w,
          paymentType: queryParams.p,
          returnMethod: queryParams[TAKEBACK_METHOD_NAME],
          returnTypeOffered: returnTypeOffered,
          store: isMailMethod ? null : queryParams[TAKEBACK_METHOD_NAME],
          originalReplayWeight: replayWeight,
          replayPercentage: queryParams.replayPercentage,
          incetiveRatio: giftRatio,
          appointmentId: appointment.id,
        });
      }}
      submitLabel={t("ui.button_submit")}
      submitTracking={[isFirstTakeback ? EVENT_13 : EVENT_14]}
      secondaryLabel={t("ui.button_back")}
      secondaryHref={
        queryParams[TAKEBACK_METHOD_NAME] === "mail"
          ? t("urls.trade_in_terms")
          : isKiosk
            ? t("urls.trade_in_appointment")
            : t("urls.trade_in_checklist")
      }
      secondaryParams={queryParams}
    >
      <FloodlightEvent tag="DC-14832541/pageview/pl_summa+per_session" />
      <ProgressBar
        step={
          currentLocation?.state === null
            ? !queryParams?.newSignIn
              ? 5
              : 6
            : queryParams[TAKEBACK_METHOD_NAME] === "mail"
              ? !queryParams?.newSignIn
                ? 6
                : 7
              : isKiosk
                ? !queryParams?.newSignIn
                  ? 5
                  : 6
                : !queryParams?.newSignIn
                  ? 4
                  : 5
        }
        showLabel
        stepsNum={
          currentLocation?.state === null
            ? !queryParams?.newSignIn
              ? 5
              : 6
            : queryParams[TAKEBACK_METHOD_NAME] === "mail"
              ? !queryParams?.newSignIn
                ? 6
                : 7
              : isKiosk
                ? !queryParams?.newSignIn
                  ? 5
                  : 6
                : !queryParams?.newSignIn
                  ? 4
                  : 5
        }
      />
      <h2 className="font-semibold text-6xl max-w-lg">
        {t("trade-in.summary.title")}
      </h2>
      <div className="flex flex-col">
        <div className="w-full flex md:border-b md:border-gray-300 py-6 pb-regular md:pb-x-large">
          <div className="w-16">
            <img src={BrickIcon} alt="" />
          </div>
          <div className="flex flex-col gap-2 w-full">
            <div className="text-2xl font-bold">
              {t("trade-in.summary.trade_in_title")}
            </div>
            <p className="text-secondary -mb-12">
              {weightEstimate} lbs of LEGO® bricks
            </p>
            <div className="text-secondary mt-12">
              {queryParams[TAKEBACK_METHOD_NAME] === "mail" &&
                Boolean(queryParams.replayPercentage) &&
                queryParams.replayPercentage >= 0 && (
                  <DonationWeightChart
                    totalWeight={queryParams.w}
                    replayWeight={replayWeight}
                    showEditLink={canEditDonation}
                    queryParams={queryParams}
                    showBackground={true}
                    rewardCaption="Reward"
                    showTotalEstimatedWeight={true}
                    replayPercentage={queryParams.replayPercentage}
                  />
                )}
              {queryParams.replayPercentage && (
                <p className="text-black-300 mt-regular">
                  You will not earn a reward for the bricks donated to LEGO
                  Replay. You'll receive a LEGO e-Gift Card for the remaining
                  bricks once verified.
                </p>
              )}
            </div>

            <WeightEstimationComponent
              className="hidden md:flex"
              queryParams={queryParams}
              currentLocation={currentLocation}
              remainingWeight={remainingWeight}
              estimate={estimate}
              weightEstimate={weightEstimate}
              unit={unit}
              methodName={methodName}
              replayWeight={replayWeight}
            />
          </div>
        </div>
        <WeightEstimationComponent
          className="mt-4 md:hidden"
          queryParams={queryParams}
          currentLocation={currentLocation}
          remainingWeight={remainingWeight}
          estimate={estimate}
          weightEstimate={weightEstimate}
          unit={unit}
          methodName={methodName}
          replayWeight={replayWeight}
        />
        <div className="w-full flex border-t mt-x-large md:mt-0 md:border-t-0 border-b border-gray-300 py-6">
          <div className="w-16">
            <img src={HeadIcon} alt="" />
          </div>
          <div className="flex flex-col gap-2 w-full">
            <div className="text-2xl font-bold">
              {t("trade-in.summary.your_details_title")}
            </div>
            <div className="text-secondary flex flex-col gap-1">
              <div>
                {user?.first_name} {user?.last_name}
              </div>
              <div>
                {userStreet && `${userStreet.trim()}, `}
                {user?.city && `${user?.city.trim()}, `}
                {user?.state && `${user?.state.trim()} `}
                {user?.zip && `${user?.zip.trim()} `}
              </div>
              <div>{user?.email}</div>
            </div>
          </div>
        </div>
        <div className="w-full flex pt-6">
          <div className="w-16">
            <span
              className={`material-symbols-outlined scale-125 text-tertiary`}
            >
              {isMailMethod ? "package_2" : "storefront"}
            </span>
          </div>
          <div className="flex flex-col gap-2 w-full">
            <div className="text-2xl font-bold">
              {isMailMethod
                ? t("trade-in.summary.mail_in")
                : isKiosk
                  ? "In-Store Appointment"
                  : "In-Store Drop-off"}
            </div>
            {!isMailMethod && (
              <div className="text-secondary">
                {isKiosk ? (
                  <div>
                    <strong>
                      {t("trade-in.summary.store_instructions")}:{" "}
                    </strong>
                    {(() => {
                      // Use new format for appointment time range
                      const startISO = appointment.time;
                      // Prefer explicit duration, otherwise recalculate
                      let duration = appointment.duration;
                      if (!duration) {
                        // fallback: recalculate from weight if needed
                        duration = parseInt(
                          typeof weight === "number" && !isNaN(weight)
                            ? calculateDurationByWeight(weight)
                            : 30
                        );
                      }
                      const formatted = formatAppointmentTime(
                        startISO,
                        queryParams[TAKEBACK_METHOD_NAME]
                      );
                      return formatted || "";
                    })()}
                  </div>
                ) : (
                  <div>
                    Pop by our store at a time and date that works for you.
                  </div>
                )}
              </div>
            )}
            <div className="text-secondary flex flex-col gap-1">
              {isMailMethod ? (
                <Fragment>
                  {t("trade-in.summary.mail_in_instructions")}
                  <a
                    href={
                      currentLocation.locale === "de-DE"
                        ? "https://www.dhl.de/de/privatkunden/dhl-standorte-finden.html"
                        : "https://www.ups.com/dropoff/?loc=en_US"
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#016db8]"
                  >
                    {t("trade-in.summary.find_nearest_ups_location")}
                  </a>
                </Fragment>
              ) : (
                <div>
                  <strong>{t("trade-in.summary.location")}</strong>:{" "}
                  {addressString}
                </div>
              )}
            </div>
          </div>
        </div>
        {validationWarning && (
          <div className="mt-x-large">
            <div className="bg-red-300 flex rounded-lg w-full h-full overflow-hidden">
              <div className="h-full w-[5px] bg-red"></div>
              <div className="flex flex-col p-4">
                <div className="font-semibold">
                  <Trans
                    i18nKey={"trade-in.summary.validation_warning_title"}
                  />
                </div>
                <div className="text-secondary text-sm">
                  <Trans
                    i18nKey={"trade-in.summary.validation_warning_body"}
                    components={{
                      1: (
                        <a href="mailto:support@lego.com" className="text-blue">
                          contact support
                        </a>
                      ),
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </FormContainer>
  );
}
